/**
 * Branded integer type for monetary amounts stored in ngwee.
 * 100 ngwee = 1 Kwacha. All DB money columns use BIGINT ngwee.
 *
 * Prevents mixing ngwee with raw numbers or kwacha at the type level.
 */
export type Ngwee = number & { readonly __brand: 'Ngwee' };

export const toNgwee = (n: number): Ngwee => {
  if (!Number.isInteger(n)) {
    throw new Error(`Ngwee must be an integer, got ${n}`);
  }
  return n as Ngwee;
};

export const kwachaToNgwee = (kwacha: number): Ngwee =>
  toNgwee(Math.round(kwacha * 100));

export const ngweeToKwacha = (ngwee: Ngwee): number => ngwee / 100;

/**
 * Discriminated union for service-layer results.
 * Services return this instead of throwing, so callers handle errors explicitly.
 */
export type ServiceResult<T, E = string> =
  | { ok: true; value: T }
  | { ok: false; error: E };

export const ok = <T>(value: T): ServiceResult<T, never> => ({ ok: true, value });
export const err = <E>(error: E): ServiceResult<never, E> => ({ ok: false, error });

/**
 * Used by React hooks that fetch data.
 */
export type FetchResult<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: T }
  | { status: 'error'; error: string };
